import * as React from 'react';
import {
  Confirm,
  Datagrid,
  FunctionField,
  List,
  useCreate,
  useDelete,
  useListContext,
  useNotify,
  useRefresh,
  useUpdate,
} from 'react-admin';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { PageHeader } from '../../components/page-header/PageHeader';
import { IconButton } from '../../components/icon-button/IconButton';
import { CardActionIcons } from '../../components/card-actions/CardActions';
import { EmptyBox } from '../../components/empty-box/EmptyBox';
import {
  VariantGroup,
  VariantGroupDialog,
  VariantTemplate,
} from '../../components/dialogs/VariantGroupDialog';
import { listSurfaceSx } from '../../core/theme/listStyles';
import { cardActionButtonSx } from '../../core/theme/buttonStyles';
import { labelAboveSx } from '../../core/theme/formStyles';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { useAccessibleLocations } from '../../hooks/useAccessibleLocations';

const MAX_ITEMS_PREVIEW = 4;

const itemsPreview = (template: VariantTemplate) => {
  const names = template.items.map((i) => i.name);
  const shown = names.slice(0, MAX_ITEMS_PREVIEW).join(', ');
  return names.length > MAX_ITEMS_PREVIEW ? `${shown} +${names.length - MAX_ITEMS_PREVIEW}` : shown;
};

const toGroup = (template: VariantTemplate): VariantGroup => ({
  key: String(template.id),
  name: template.name,
  isRequired: template.isRequired,
  isSingleChoice: template.isSingleChoice,
  items: template.items,
});

const TemplateChips = ({ template }: { template: VariantTemplate }) => {
  const { t } = useTranslation();
  const dialogKeys = 'locationsManagement.createItem.variantGroupDialog';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
      <Chip
        size="small"
        label={template.isSingleChoice ? t(`${dialogKeys}.singleChoice`) : t(`${dialogKeys}.multipleChoice`)}
      />
      {template.isRequired && <Chip size="small" color="primary" label={t(`${dialogKeys}.required`)} />}
    </Box>
  );
};

interface ListBodyProps {
  onEdit: (template: VariantTemplate) => void;
  onDelete: (template: VariantTemplate) => void;
}

const MobileTemplatesList = ({ onEdit, onDelete }: ListBodyProps) => {
  const { data, isLoading } = useListContext<VariantTemplate>();
  const { t } = useTranslation();

  if (isLoading || !data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {data.map((template) => (
        <Box
          key={template.id}
          sx={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            p: 'var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 'var(--text-sm)', flex: 1, minWidth: 0 }}>
              {template.name}
            </Typography>
            <CardActionIcons
              actions={[
                {
                  key: 'edit',
                  label: t('common.edit'),
                  icon: <EditIcon fontSize="small" />,
                  onClick: () => onEdit(template),
                },
                {
                  key: 'delete',
                  label: t('common.delete'),
                  icon: <DeleteOutlineIcon fontSize="small" />,
                  destructive: true,
                  onClick: () => onDelete(template),
                },
              ]}
            />
          </Box>
          <TemplateChips template={template} />
          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
            {itemsPreview(template)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const DesktopTemplatesList = ({ onEdit, onDelete }: ListBodyProps) => {
  const { t } = useTranslation();
  return (
    <Datagrid
      bulkActionButtons={false}
      rowClick={false}
      sx={{ '& .RaDatagrid-headerCell': { fontWeight: 700, letterSpacing: 0.2 } }}
    >
      <FunctionField
        sortable={false}
        label={t('variantTemplates.columns.name')}
        render={(tpl: VariantTemplate) => (
          <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{tpl.name}</Typography>
        )}
      />
      <FunctionField
        sortable={false}
        label={t('variantTemplates.columns.type')}
        render={(tpl: VariantTemplate) => <TemplateChips template={tpl} />}
      />
      <FunctionField
        sortable={false}
        label={t('variantTemplates.columns.items')}
        render={(tpl: VariantTemplate) => (
          <Tooltip title={tpl.items.map((i) => i.name).join(', ')}>
            <span>{itemsPreview(tpl)}</span>
          </Tooltip>
        )}
      />
      <FunctionField
        label=""
        sortable={false}
        render={(tpl: VariantTemplate) => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton icon={<EditIcon />} onClick={() => onEdit(tpl)} />
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton icon={<DeleteOutlineIcon />} color="var(--color-error)" onClick={() => onDelete(tpl)} />
              </span>
            </Tooltip>
          </Box>
        )}
      />
    </Datagrid>
  );
};

export const VariantTemplatesPage = () => {
  const { t } = useTranslation();
  const notify = useNotify();
  const refresh = useRefresh();
  const isMobile = useMediaQuery('(max-width:600px)');
  const { locations, loading: locationsLoading } = useAccessibleLocations();
  const [locationId, setLocationId] = React.useState<number | null>(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<VariantTemplate | null>(null);
  const [toDelete, setToDelete] = React.useState<VariantTemplate | null>(null);
  const keyRef = React.useRef(0);

  // Stabilna referencja — dialog resetuje formularz przy kazdej zmianie `initialGroup`.
  const editingGroup = React.useMemo(() => (editing ? toGroup(editing) : null), [editing]);

  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();

  // Wlasciciel/vendor ma jedna lokalizacje, admin startuje od pierwszej z listy.
  React.useEffect(() => {
    if (locationId === null && locations.length > 0) setLocationId(locations[0].id);
  }, [locations, locationId]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (template: VariantTemplate) => {
    setEditing(template);
    setDialogOpen(true);
  };

  const handleSave = async (group: VariantGroup) => {
    const data = {
      locationId,
      name: group.name,
      isSingleChoice: group.isSingleChoice,
      isRequired: group.isRequired,
      items: group.items,
    };
    try {
      if (editing) {
        await update(
          'variantTemplates',
          { id: editing.id, data: { ...editing, ...data }, previousData: editing },
          { returnPromise: true },
        );
        notify(t('variantTemplates.notifications.updated'), { type: 'success' });
      } else {
        await create('variantTemplates', { data }, { returnPromise: true });
        notify(t('variantTemplates.notifications.created'), { type: 'success' });
      }
      refresh();
    } catch (error: any) {
      notify(error?.body?.error ?? error?.message, { type: 'error' });
      Sentry.captureException(error);
    }
  };

  return (
    <>
      <PageHeader
        stretchActionsOnMobile
        title={t('resources.variantTemplates')}
        subtitle={t('variantTemplates.subtitle')}
        actions={
          <IconButton
            icon={<AddIcon />}
            label={t('variantTemplates.addTemplate')}
            sx={isMobile ? cardActionButtonSx : undefined}
            onClick={openCreate}
          />
        }
      />

      {locationsLoading || locationId === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 'var(--space-16)' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {locations.length > 1 && (
            <Box sx={{ px: PAGE_GUTTER, pb: 'var(--space-4)', maxWidth: 400, ...labelAboveSx }}>
              <FormControl size="small" fullWidth>
                <InputLabel shrink>{t('variantTemplates.location')}</InputLabel>
                <Select value={locationId} onChange={(e) => setLocationId(Number(e.target.value))}>
                  {locations.map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <List
            key={locationId}
            resource="variantTemplates"
            filter={{ locationId }}
            sort={{ field: 'name', order: 'ASC' }}
            actions={false}
            perPage={50}
            disableSyncWithLocation
            storeKey={false}
            empty={
              <EmptyBox
                title={t('variantTemplates.emptyBox.title')}
                description={t('variantTemplates.emptyBox.description')}
                onClick={openCreate}
              />
            }
            sx={listSurfaceSx({ isMobile })}
          >
            {isMobile ? (
              <MobileTemplatesList onEdit={openEdit} onDelete={setToDelete} />
            ) : (
              <DesktopTemplatesList onEdit={openEdit} onDelete={setToDelete} />
            )}
          </List>
        </>
      )}

      <VariantGroupDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initialGroup={editingGroup}
        makeKey={() => `t${++keyRef.current}`}
        templateMode
      />

      <Confirm
        isOpen={toDelete !== null}
        title={t('variantTemplates.dialog.confirmDeletionTitle')}
        content={t('variantTemplates.dialog.confirmDeletionBody', { name: toDelete?.name ?? '' })}
        confirm={t('common.delete')}
        cancel={t('common.cancel')}
        onConfirm={async () => {
          if (!toDelete) return;
          try {
            await deleteOne('variantTemplates', { id: toDelete.id, previousData: toDelete }, { returnPromise: true });
            notify(t('variantTemplates.notifications.deleted'), { type: 'success' });
            refresh();
          } catch (error: any) {
            notify(error?.body?.error ?? error?.message, { type: 'error' });
            Sentry.captureException(error);
          } finally {
            setToDelete(null);
          }
        }}
        onClose={() => setToDelete(null)}
      />
    </>
  );
};

export default VariantTemplatesPage;
